import api from '@/api';

export type Paged<T> = { success: boolean; data: T[]; total: number; page: number; limit: number };

export type AdminOrganization = {
    _id: string;
    orgName: string;
    shortName?: string;
    profileUrl?: string;
    about?: string;
    email?: string;
    phone?: string;
    address?: string;
    lat?: string;
    lng?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    facebook?: string;
    testMode?: boolean;
    feeType?: 'percent' | 'fixed' | 'tiered';
    feeValue?: number;
    feeLabel?: string;
    feeTiers?: Array<{ upToPrice: number | null; feeType: 'percent' | 'fixed'; feeValue: number }>;
    taxRate?: number;
    taxMode?: 'added' | 'included';
    balance?: number;
    pendingBalance?: number;
    authorizationEmails?: Array<{ email: string; role: 'admin' | 'eventmanager' | 'usher' }>;
    teamCount?: number;
    eventCount?: number;
    owner?: { email?: string; firstName?: string; lastName?: string } | null;
    createdAt?: string;
};

export type AdminUser = {
    _id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    username?: string;
    status?: string;
    isAdmin: boolean;
    organizations: Array<{ organizerId: string; orgName: string; role: string }>;
    createdAt?: string;
};

export type AdminEvent = {
    _id: string;
    title: string;
    status?: string;
    organizerId?: string;
    organizerName?: string | null;
    startDate?: string | null;
    venue?: string;
    isPrivate?: number | boolean;
    isDeleted?: boolean;
    createdAt?: string;
};

export type OrganizationReportRow = {
    organizerId: string;
    orgName: string;
    testMode: boolean;
    events: number;
    orders: number;
    tickets: number;
    revenue: number;
    averageOrderValue: number;
};

export type OrganizationReport = {
    rows: OrganizationReportRow[];
    totals: { organizations: number; events: number; orders: number; tickets: number; revenue: number };
};

type ListParams = { page?: number; limit?: number; search?: string };

export const fetchAdminOrganizations = async (params: ListParams): Promise<Paged<AdminOrganization>> =>
    (await api.get('/eventopia/admin/organizations', { params })).data;

export const fetchAdminOrganization = async (id: string): Promise<AdminOrganization> =>
    (await api.get(`/eventopia/admin/organizations/${id}`)).data.data;

export const updateAdminOrganization = async (id: string, body: Partial<AdminOrganization>): Promise<AdminOrganization> =>
    (await api.put(`/eventopia/admin/organizations/${id}`, body)).data.data;

export const fetchAdminUsers = async (params: ListParams & { role?: 'admin' | 'member' }): Promise<Paged<AdminUser>> =>
    (await api.get('/eventopia/admin/users', { params })).data;

export const updateAdminUser = async (id: string, body: { firstName?: string; lastName?: string; phone?: string; status?: string; isAdmin?: boolean }): Promise<AdminUser> =>
    (await api.put(`/eventopia/admin/users/${id}`, body)).data.data;

export type AdminEventDetail = AdminEvent & {
    description?: string;
    category?: string;
    email?: string;
    handle?: string;
    commentsDisabled?: number;
    refundable?: boolean;
    refundDaysBeforeStart?: number;
    feeMode?: 'added' | 'included';
    taxMode?: 'added' | 'included';
    currency?: 'usd' | 'cad';
    venueId?: string | null;
    isDeleted?: boolean;
};

export const fetchAdminEvent = async (id: string): Promise<AdminEventDetail> =>
    (await api.get(`/eventopia/admin/events/${id}`)).data.data;

export const updateAdminEvent = async (id: string, body: Partial<AdminEventDetail>): Promise<AdminEventDetail> =>
    (await api.put(`/eventopia/admin/events/${id}`, body)).data.data;

export const fetchAdminEvents = async (params: ListParams & { organizerId?: string; status?: string; deleted?: boolean }): Promise<Paged<AdminEvent>> =>
    (await api.get('/eventopia/admin/events', { params })).data;

export const fetchOrganizationReport = async (params: { from?: string; to?: string }): Promise<OrganizationReport> =>
    (await api.get('/eventopia/admin/reports/organizations', { params })).data.data;

export const clearAdminCache = async (prefix?: string): Promise<{ cleared: number | 'all'; prefix: string | null }> =>
    (await api.delete('/eventopia/admin/cache', { params: { prefix: prefix || undefined } })).data.data;

export type AuditLogEntry = {
    _id: string;
    actorEmail?: string;
    actorName?: string;
    impersonatedBy?: string;
    method: string;
    path: string;
    resource?: string;
    resourceId?: string;
    body?: unknown;
    changes?: Record<string, { before: unknown; after: unknown }>;
    uploaded?: string[];
    createdAt: string;
};

export const fetchAuditLogs = async (params: ListParams & { resource?: string; resourceId?: string; method?: string; from?: string; to?: string }): Promise<Paged<AuditLogEntry>> =>
    (await api.get('/eventopia/admin/audit-logs', { params })).data;

export type ActiveSession = { id: string; email?: string; device?: string; ip?: string; createdAt?: string; lastSeenAt: string };

export type GuestVisitor = {
    id: string;
    guestId: string;
    device?: string;
    ip?: string;
    lastPath?: string;
    pings: number;
    views: Array<{ path: string; at: string }>;
    lastSeenAt: string;
};

export type ActivitySummary = {
    guests: { last10m: number; last30m: number; last24h: number; last7d: number; last30d: number; recent: GuestVisitor[] };
    listWindowMinutes: number;
    activeSessions: number;
    last10m: number;
    last30m: number;
    last24h: number;
    last7d: number;
    last30d: number;
    recent: ActiveSession[];
};

export const fetchActivity = async (windowMinutes: number): Promise<ActivitySummary> =>
    (await api.get('/eventopia/admin/activity', { params: { windowMinutes } })).data.data;

export const revokeSession = async (id: string) => (await api.delete(`/eventopia/admin/sessions/${id}`)).data.data;

export const revokeUserSessions = async (email: string) => (await api.delete(`/eventopia/admin/users/${encodeURIComponent(email)}/sessions`)).data.data;

export type UserSession = { id: string; device?: string; ip?: string; createdAt?: string; lastSeenAt: string; current?: boolean };

export const fetchMySessions = async (): Promise<UserSession[]> => (await api.get('/auth/sessions')).data;
export const revokeMySession = async (id: string) => (await api.delete(`/auth/sessions/${id}`)).data;
export const revokeOtherSessions = async () => (await api.post('/auth/sessions/revoke-others')).data;
export const fetchUserSessions = async (email: string): Promise<UserSession[]> =>
    (await api.get(`/eventopia/admin/users/${encodeURIComponent(email)}/sessions`)).data.data;
